/* ----------------- Globals --------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { Box, Button } from "@memorang/ui";

/* ----------------- Third parties --------------- */
import { useEffect, useRef, useState } from "react";
import { Card, IconButton, Text } from "react-native-paper";
import useHeyGenAvatar from "../hooks/useHeyGenAvatar";

import ChatInput from "../components/ChatInput";
import ConversationTranscript from "../components/ConversationTranscript";

/* ----------------- Types --------------- */
interface Props {
	itemId: number;
	isAnswered?: boolean;
}

const InteractiveVideoContainer = ({ itemId, isAnswered }: Props) => {
	const [inputText, setInputText] = useState("");
	const [isEnded, setIsEnded] = useState(false);
	const hasAutoStartedRef = useRef(false);

	const {
		status,
		sessionState,
		isSessionActive,
		mediaElementRef,
		isListening,
		isAvatarSpeaking,
		isMicrophoneEnabled,
		transcript,
		startStreaming,
		stopStreaming,
		sendText,
		toggleMicrophone,
		canStartSession,
		canStopSession,
	} = useHeyGenAvatar(itemId);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (hasAutoStartedRef.current || !canStartSession || isAnswered) {
			return;
		}

		hasAutoStartedRef.current = true;
		(async () => {
			await startStreaming();
		})();
	}, [canStartSession, isAnswered]);

	const handleStart = async () => {
		setIsEnded(false);
		hasAutoStartedRef.current = true;
		await startStreaming();
	};

	const handleSendText = async () => {
		if (!inputText.trim()) {
			return;
		}

		await sendText(inputText.trim());
		setInputText("");
	};

	const handleClose = async () => {
		await stopStreaming();
		setIsEnded(true);
	};

	const getStatusColor = () => {
		if (sessionState === "connecting" || sessionState === "disconnecting") {
			return "#ffb020";
		}

		if (sessionState === "error") {
			return "#ff3b30";
		}

		if (!isSessionActive) {
			return "#9e9e9e";
		}

		return "#4CAF50";
	};

	return (
		<Card
			style={{
				borderRadius: 3,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Card.Content
				style={{
					borderBottomWidth: 1,
					borderBottomColor: "#e0e0e0",
					padding: 16,
				}}
			>
				<Box
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Box display="flex" flexDirection="row" alignItems="center" gap={8}>
						<Text variant="titleLarge">Virtual Patient Consultation</Text>
						<Box
							style={{
								width: 8,
								height: 8,
								borderRadius: 4,
								backgroundColor: getStatusColor(),
								marginLeft: 8,
							}}
						/>
					</Box>
					<Box display="flex" flexDirection="row" gap={8}>
						{canStartSession && !isAnswered && (
							<Button
								onPress={handleStart}
								mode="outlined"
								disabled={isAnswered || sessionState === "connecting"}
							>
								Start Session
							</Button>
						)}
						{canStopSession && !isAnswered && (
							<Button
								onPress={handleClose}
								mode="outlined"
								color="error"
								disabled={isAnswered}
							>
								End Session
							</Button>
						)}
					</Box>
				</Box>
			</Card.Content>

			<Box
				style={{
					height: 300,
					backgroundColor: "#f5f5f5",
					paddingTop: 4,
				}}
			>
				{isSessionActive ? (
					<video
						ref={mediaElementRef}
						autoPlay
						playsInline
						controls
						style={{ width: "100%", height: "100%", objectFit: "contain" }}
						aria-label="Avatar stream"
					>
						<track
							kind="captions"
							src=""
							srcLang="en"
							label="English captions"
							default
						/>
					</video>
				) : isEnded ? (
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						height="100%"
						gap={16}
					>
						<Icon name="check-circle" size={64} color="#4CAF50" />
						<Text variant="titleMedium" style={{ textAlign: "center" }}>
							Session completed!
						</Text>
						<Text style={{ textAlign: "center", color: "#666" }}>
							Your responses have been recorded for evaluation
						</Text>
					</Box>
				) : (
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						height="100%"
						gap={16}
					>
						<Box style={{ position: "relative" }}>
							<Icon name="robot" size={64} color="#aaaaaa" />
						</Box>
						<Text variant="titleMedium">
							{sessionState === "connecting"
								? "Connecting to virtual patient..."
								: "Session not started"}
						</Text>
					</Box>
				)}
			</Box>

			<Box
				style={{
					height: 400,
				}}
			>
				<ConversationTranscript
					messages={transcript.map((message) => ({
						type: message.type,
						text: message.message,
						timestamp: message.timestamp,
					}))}
				/>
			</Box>

			<Card.Actions
				style={{
					padding: 16,
					justifyContent: "space-between",
				}}
			>
				<Box style={{ flex: 1, marginRight: 16 }}>
					<ChatInput
						value={inputText}
						onChangeText={setInputText}
						onSend={handleSendText}
						disabled={!isSessionActive || isAnswered}
					/>
				</Box>
				<Box display="flex" flexDirection="row" alignItems="center" gap={8}>
					{isListening && (
						<Text style={{ color: "#2e7d32" }} variant="bodySmall">
							Listening
						</Text>
					)}
					{isAvatarSpeaking && (
						<Text style={{ color: "#1565c0" }} variant="bodySmall">
							Avatar speaking
						</Text>
					)}
					<IconButton
						icon={isMicrophoneEnabled ? "microphone" : "microphone-off"}
						mode="contained"
						size={24}
						onPress={toggleMicrophone}
						disabled={!isSessionActive || isAnswered}
						style={{
							backgroundColor: isMicrophoneEnabled ? "#e8f5e9" : "#f0f0f0",
						}}
					/>
				</Box>
			</Card.Actions>
			<Card.Content style={{ paddingTop: 0, paddingBottom: 12 }}>
				<Text variant="bodySmall" style={{ color: "#666" }}>
					{status}
				</Text>
			</Card.Content>
		</Card>
	);
};

export default InteractiveVideoContainer;
